import { createContext } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router';

import {
  useGetProjectQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { Route } from 'src/shared/route';

import type {
  CustomizationCategory,
  MaterialRegionOrPaint,
} from '../../constants/customizationCategories';
import type {
  DesignerPartner,
  PartnerMetadata,
} from '../../constants/partners';
import { NO_PARTNER_METADATA } from '../../constants/partners';
import type { AiDisplayImageFragment } from '../../graphql/generated';
import {
  useAiDisplayImageProjectPartnerQuery,
  useProductCatalogCustomizeCategoriesQuery,
  useProductCatalogFacetsQuery,
} from '../../graphql/generated';
import type {
  AiOriginalImageWithPartnerInfo,
  ProjectWithPartnerInfo,
} from '../../helpers/partners';
import {
  getHomeownerOrgId,
  publicOrgInfoToPartnerMetadata,
} from '../../helpers/partners';

export interface DesignerPartnerContext {
  isLoading: boolean;
  metadata: PartnerMetadata | null;
}

export interface PartnerCobranding {
  displayName?: string;
  logoUrl?: string;
  logoUrlInvert?: string;
  types?: string[];
  orgId?: number;
  partner: DesignerPartner;
  categories: CustomizationCategory[] | null;
  nonPartnerPalettes: string[];
}

const [Provider, useContext] = createContext<
  DesignerPartnerContext | undefined
>({
  name: 'DesignerPartnerContext',
  strict: false,
});

//
// Public hooks
//

/**
 * Access the current partner association with loading state. This returns the
 * `DesignerPartner` enum which can be used to access other hard-coded partner
 * configuration.
 *
 * - If you need the partner cobranding label and logo, use
 *   `usePartnerCobranding`
 * - If you need the hard-coded partner configuration from
 *   `PARTNER_CONFIGURATION_MAP`, use `usePartnerConfiguration`
 *
 * NOTE: this requires the `DesignerPartnerContext` to be provided by a parent.
 * Because the partner association is accessed in a few different ways in our
 * data model, different providers must be used for different data contexts:
 *
 * @see {@link PartnerImageProvider} in relation to an `AiDisplayImage`
 * @see {@link PartnerProjectProvider} in relation to a `Project`
 * @see {@link PartnerOriginalImageProvider} in relation to a `AiOriginalImage`
 */
export const usePartner = () => {
  const context = useContext();

  if (!context) {
    return {
      partner: null,
      isLoading: false,
    };
  }

  const { isLoading, metadata } = context;

  return {
    isLoading,
    partner: metadata?.partner,
  };
};

const categoriesForBrandTypes = (
  brandTypes: MaterialRegionOrPaint[] | undefined,
): CustomizationCategory[] | null => {
  if (!brandTypes || brandTypes.length === 0) return null;

  const categories: CustomizationCategory[] = [];

  if (brandTypes.includes('paint')) {
    categories.push({ type: 'paint', region: 'walls' });
  }

  const materialRegions = brandTypes.filter(region => region !== 'paint');

  categories.push(
    ...materialRegions.map(
      region =>
        ({
          type: 'material',
          region,
        } as CustomizationCategory),
    ),
  );

  return categories;
};

/**
 * Get the cobranding information for the current associated partner (if any).
 *
 * This hook returns `null` while loading the data necessary to determine the
 * partner and when there is no partner association. Use `isLoading` from
 * `usePartner` if you need to access the loading state.
 */
export const usePartnerCobranding = () => {
  const context = useContext();

  if (!context || !context.metadata) return null;

  const {
    displayName,
    logoUrl,
    logoUrlInvert,
    configuration: { brandTypes, nonPartnerPalettes },
    orgId,
    partner,
  } = context.metadata;

  return {
    displayName,
    logoUrl,
    logoUrlInvert,
    types: brandTypes,
    orgId,
    partner,
    nonPartnerPalettes: nonPartnerPalettes ?? [],
    categories: categoriesForBrandTypes(brandTypes),
  } satisfies PartnerCobranding;
};

/**
 * Get the hard-coded partner configuration from `PARTNER_CONFIGURATION_MAP` for
 * the current associated partner (if any).
 *
 * This hook returns `null` while loading the data necessary to determine the
 * partner and when there is no partner association. Use `isLoading` from
 * `usePartner` if you need to access the loading state.
 */
export const usePartnerConfiguration = () => {
  const context = useContext();

  if (!context || !context.metadata || context.isLoading) return null;

  return context.metadata?.configuration;
};

/**
 * Returns the org id of the contextual partner
 */
export const usePartnerOrgId = () => {
  const context = useContext();

  return context?.metadata?.orgId;
};

/**
 * Query Product Catalog categories for the partner context. If there is no
 * partner, query the default set of Product Catalog categories.
 *
 * This hook combines the loading state of the partner context and the categories
 * query.
 */
export const usePartnerCustomizeCategories = () => {
  const context = useContext();
  const orgId = usePartnerOrgId() ?? getHomeownerOrgId();

  const skip = !context || !context.metadata || context.isLoading;
  const {
    data,
    loading: categoriesLoading,
    error,
  } = useProductCatalogCustomizeCategoriesQuery({
    variables: { ...(orgId ? { orgId: String(orgId) } : undefined) },
    skip,
  });

  const loading = categoriesLoading || (context?.isLoading ?? false);

  return { data: data?.genAiCategories.nodes, loading, error };
};

/**
 * Query Product Catalog facets for the partner context. If there is no
 * partner, query the default set of Product Catalog facets.
 *
 * This hook combines the loading state of the partner context and the facets
 * query.
 * @param productCatalogCategoryId
 */
export const usePartnerFacets = (
  productCatalogCategoryId: string | undefined,
) => {
  const context = useContext();
  const orgId = usePartnerOrgId() ?? getHomeownerOrgId();
  const skip =
    !context ||
    !context.metadata ||
    context.isLoading ||
    !orgId ||
    !productCatalogCategoryId;

  const queryResult = useProductCatalogFacetsQuery({
    skip,
    variables: {
      genAiOrgId: orgId ? String(orgId) : '',
      productCatalogCategoryId: productCatalogCategoryId ?? '',
    },
  });

  const loading = queryResult.loading || (context?.isLoading ?? false);

  return useMemo(() => ({ ...queryResult, loading }), [queryResult, loading]);
};

//
// Providers
//

interface UsePartnerOptions {
  skip?: boolean;
}

interface UsePartnerForImageOptions extends UsePartnerOptions {
  aiDisplayImageId: AiDisplayImageFragment['id'];
}

const usePartnerForImage = ({
  aiDisplayImageId,
  skip,
}: UsePartnerForImageOptions): DesignerPartnerContext => {
  const { data: imageData, loading: isImageLoading } =
    useAiDisplayImageProjectPartnerQuery({
      variables: {
        id: aiDisplayImageId,
      },
      skip,
    });

  return useMemo(() => {
    if (isImageLoading) {
      return { metadata: NO_PARTNER_METADATA, isLoading: true };
    }

    const image = imageData?.aiDisplayImage;

    return {
      metadata: image
        ? publicOrgInfoToPartnerMetadata(
            image.aiOriginalImage?.associatedProject?.publicOrgInfo,
          )
        : NO_PARTNER_METADATA,
      isLoading: false,
    };
  }, [imageData, isImageLoading]);
};

export interface PartnerImageProviderProps
  extends PropsWithChildren<{}>,
    UsePartnerForImageOptions {}

/**
 * Given an AI display image ID, fetch the partner associated with that image
 * and make it available via `usePartner`
 */
export const PartnerImageProvider = ({
  aiDisplayImageId,
  skip,
  ...props
}: PartnerImageProviderProps) => (
  <Provider value={usePartnerForImage({ aiDisplayImageId, skip })} {...props} />
);

interface UsePartnerForProjectOptions extends UsePartnerOptions {
  project: ProjectWithPartnerInfo | undefined | null;
}

const getPartnerForProject = ({ project }: UsePartnerForProjectOptions) => ({
  metadata: project
    ? publicOrgInfoToPartnerMetadata(project.publicOrgInfo)
    : NO_PARTNER_METADATA,
  isLoading: false,
});

const usePartnerForProject = ({
  project,
}: UsePartnerForProjectOptions): DesignerPartnerContext =>
  useMemo(() => getPartnerForProject({ project }), [project]);

export interface PartnerProjectProviderProps
  extends PropsWithChildren<{}>,
    UsePartnerForProjectOptions {}

/**
 * Given a project that includes `publicOrgInfo`, fetch the partner associated
 * with that project and make it available via `usePartner`
 */
export const PartnerProjectProvider = ({
  project,
  ...props
}: PartnerProjectProviderProps) => (
  <Provider value={usePartnerForProject({ project })} {...props} />
);

interface UsePartnerForOriginalImageOptions extends UsePartnerOptions {
  aiOriginalImage: AiOriginalImageWithPartnerInfo | null | undefined;
}

export const getPartnerForOriginalImage = ({
  aiOriginalImage,
}: UsePartnerForOriginalImageOptions): DesignerPartnerContext => ({
  metadata: aiOriginalImage
    ? publicOrgInfoToPartnerMetadata(
        aiOriginalImage?.associatedProject?.publicOrgInfo,
      )
    : NO_PARTNER_METADATA,
  isLoading: false,
});

const usePartnerForOriginalImage = ({
  aiOriginalImage,
}: UsePartnerForOriginalImageOptions): DesignerPartnerContext =>
  useMemo(
    () => getPartnerForOriginalImage({ aiOriginalImage }),
    [aiOriginalImage],
  );

export interface PartnerOriginalImageProviderProps
  extends PropsWithChildren<{}>,
    UsePartnerForOriginalImageOptions {}

/**
 * Given an original image that includes `publicOrgInfo`, fetch the partner
 * associated with that original image and make it available via `usePartner`
 */
export const PartnerOriginalImageProvider = ({
  aiOriginalImage,
  ...props
}: PartnerOriginalImageProviderProps) => (
  <Provider
    value={usePartnerForOriginalImage({ aiOriginalImage })}
    {...props}
  />
);

interface UsePartnerForAffiliateOrgIdOptions extends UsePartnerOptions {
  affOrgId: string | null | undefined;
}

const usePartnerForAffiliateOrgId = ({
  affOrgId,
}: UsePartnerForAffiliateOrgIdOptions): DesignerPartnerContext => {
  const { data: publicOrgInfoData, loading: publicOrgInfoLoading } =
    usePublicOrgInfoQuery({
      variables: {
        publicIdentifier: affOrgId || '',
      },
      skip: !affOrgId,
    });

  return useMemo(
    () => ({
      metadata: publicOrgInfoToPartnerMetadata(
        publicOrgInfoData?.publicOrgInfo,
      ),
      isLoading: publicOrgInfoLoading,
    }),
    [publicOrgInfoData, publicOrgInfoLoading],
  );
};

export interface PartnerAffiliateOrgIdProps
  extends PropsWithChildren<{}>,
    UsePartnerForAffiliateOrgIdOptions {}

/**
 * Given a public affiliate org id, fetch the partner
 * associated with that org id and make it available via `usePartner`
 */
export const PartnerAffiliateOrgIdProvider = ({
  affOrgId,
  ...props
}: PartnerAffiliateOrgIdProps) => (
  <Provider value={usePartnerForAffiliateOrgId({ affOrgId })} {...props} />
);

export type PartnerProviderProps = PartnerAffiliateOrgIdProps &
  PartnerImageProviderProps & { shouldUseImage?: boolean };

/**
 * Given we have an affiliate org id or an ai display image id, fetch the partner.
 * In some places, we want to use the affOrgId to fetch partner information and not the image,
 * for example in embed.
 */
export const PartnerProvider = ({
  affOrgId,
  aiDisplayImageId,
  shouldUseImage = true,
  ...props
}: PartnerProviderProps) => {
  if (shouldUseImage) {
    return (
      <PartnerImageProvider {...props} aiDisplayImageId={aiDisplayImageId} />
    );
  }

  return <PartnerAffiliateOrgIdProvider {...props} affOrgId={affOrgId} />;
};

interface PartnerAffiliateByRouteProps extends PropsWithChildren<{}> {}

/**
 * given the context of the active project route, fetch the partner
 * associated with that project id and make it available via `usepartner`
 */
export const PartnerAffiliateByRoute = ({
  ...props
}: PartnerAffiliateByRouteProps) => {
  const match = useRouteMatch<{ projectId?: string }>([
    Route.HOMEOWNER_APP_PROJECT.path,
    Route.HOMEOWNER_APP_PROJECT_IDEAS.path,
    Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
    Route.EMBED_PROJECT.path,
  ]);
  const { data: projectData } = useGetProjectQuery({
    variables: { projectId: Number(match?.params?.projectId) },
    skip: !match?.params?.projectId,
  });
  const { project } = projectData || {};

  return <Provider value={usePartnerForProject({ project })} {...props} />;
};
